/**
 * StatusDonut — donut + legend for the loan-status breakdown.
 *
 * The SVG draws each slice as a thick stroked arc on a circle; the
 * legend below pairs colour swatches with counts. Pure component;
 * the parent decides which palette to use per status.
 */
import { useMemo } from "react";

interface DonutSegment {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly colour: string;
}

interface Props {
  readonly segments: readonly DonutSegment[];
  readonly heightPx?: number;
}

const VIEW = 100;
const RADIUS = 40;
const STROKE = 14;

export function StatusDonut({ segments, heightPx = 180 }: Props): JSX.Element {
  const total = useMemo(() => segments.reduce((sum, s) => sum + s.value, 0), [segments]);

  if (total === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-simuflex-slate"
        style={{ height: heightPx }}
      >
        No loans in this window.
      </div>
    );
  }

  // Compute (start, end) angles for each segment. SVG `stroke-dasharray`
  // takes (visible, gap), which is the easiest way to draw an arc on a
  // circle without computing a complex `d` path. Circumference = 2πr.
  const circumference = 2 * Math.PI * RADIUS;
  let offset = 0;
  const arcs = segments.map((s) => {
    const fraction = s.value / total;
    const dash = fraction * circumference;
    const gap = circumference - dash;
    const arc = {
      ...s,
      dash,
      gap,
      offset,
    };
    offset += dash;
    return arc;
  });

  return (
    <div className="flex flex-col items-center gap-3">
      <svg
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        style={{ height: heightPx, width: heightPx }}
        role="img"
        aria-label="Status donut chart"
      >
        {/* Background ring so empty space is visible even when the
            dominant slice is small. */}
        <circle
          cx={VIEW / 2}
          cy={VIEW / 2}
          r={RADIUS}
          fill="none"
          stroke="#E1E6EC"
          strokeWidth={STROKE}
        />
        {arcs.map((arc) => (
          <circle
            key={arc.key}
            cx={VIEW / 2}
            cy={VIEW / 2}
            r={RADIUS}
            fill="none"
            stroke={arc.colour}
            strokeWidth={STROKE}
            strokeDasharray={`${arc.dash} ${arc.gap}`}
            strokeDashoffset={-arc.offset}
            // Rotate -90° so the donut starts at 12 o'clock (the
            // first segment grows clockwise from the top).
            transform={`rotate(-90 ${VIEW / 2} ${VIEW / 2})`}
          />
        ))}
        {/* Centre label = total. */}
        <text
          x={VIEW / 2}
          y={VIEW / 2 - 2}
          textAnchor="middle"
          fontSize={14}
          fontWeight={800}
          fill="#1A3B69"
        >
          {total}
        </text>
        <text
          x={VIEW / 2}
          y={VIEW / 2 + 9}
          textAnchor="middle"
          fontSize={6}
          fontWeight={700}
          fill="#6C757D"
          letterSpacing={1}
        >
          LOANS
        </text>
      </svg>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        {segments.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: s.colour }}
            />
            <span className="font-semibold text-simuflex-navy">{s.label}</span>
            <span className="text-simuflex-slate">{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
